<template>
  <div class="media-preview">
    <!-- 媒体文件显示 -->
    <div class="media-container" v-if="mediaUrl">
      <!-- 图片类型 -->
      <el-image 
        v-if="isImage"
        style="width: 60px; height: 60px" 
        :src="mediaUrl"
        :preview-src-list="[mediaUrl]"
        fit="cover">
        <div slot="error" class="image-slot">
          <i class="el-icon-picture-outline"></i>
        </div>
      </el-image>
      
      <!-- 视频类型 -->
      <div v-else-if="isVideo" class="video-container">
        <video 
          :src="mediaUrl" 
          style="width: 60px; height: 60px; object-fit: cover; cursor: pointer"
          @click="showVideoPreview = true"
          muted
          preload="metadata">
        </video>
        <div class="video-overlay" @click="showVideoPreview = true">
          <i class="el-icon-video-play"></i>
        </div>
      </div>
      
      <!-- 其他文件类型 -->
      <div v-else class="file-container">
        <i class="el-icon-document" style="font-size: 40px; color: #909399;"></i>
        <p class="file-name">{{ getFileName(mediaUrl) }}</p>
      </div>
      
      <!-- 操作按钮 -->
      <div class="media-actions">
        <el-tooltip content="预览" placement="top">
          <el-button 
            type="primary" 
            icon="el-icon-view" 
            size="mini" 
            circle 
            @click="previewMedia">
          </el-button>
        </el-tooltip>
        <el-tooltip content="下载" placement="top">
          <el-button 
            type="success" 
            icon="el-icon-download" 
            size="mini" 
            circle 
            @click="downloadMedia">
          </el-button>
        </el-tooltip>
      </div>
    </div>
    
    <!-- 无媒体文件 -->
    <div v-else class="no-media">
      <span>无文件</span>
    </div>

    <!-- 视频预览对话框 -->
    <el-dialog 
      title="视频预览" 
      :visible.sync="showVideoPreview" 
      width="80%"
      :before-close="handleVideoClose">
      <div class="video-preview-container">
        <video 
          v-if="showVideoPreview && isVideo"
          :src="mediaUrl" 
          controls 
          style="width: 100%; max-height: 70vh;"
          @loadstart="videoLoading = true"
          @canplay="videoLoading = false"
          @error="handleVideoError">
        </video>
        <div v-if="videoLoading" class="video-loading">
          <i class="el-icon-loading"></i>
          <span>视频加载中...</span>
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览对话框（自定义） -->
    <el-dialog 
      title="图片预览" 
      :visible.sync="showImagePreview" 
      width="80%">
      <div class="image-preview-container">
        <img 
          v-if="showImagePreview && isImage"
          :src="mediaUrl" 
          style="width: 100%; max-height: 70vh; object-fit: contain;"
          @error="handleImageError">
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'MediaPreview',
  props: {
    mediaUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showVideoPreview: false,
      showImagePreview: false,
      videoLoading: false
    }
  },
  computed: {
    isImage() {
      if (!this.mediaUrl) return false
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const url = this.mediaUrl.toLowerCase()
      return imageExtensions.some(ext => url.includes(ext))
    },
    isVideo() {
      if (!this.mediaUrl) return false
      const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv']
      const url = this.mediaUrl.toLowerCase()
      return videoExtensions.some(ext => url.includes(ext))
    }
  },
  methods: {
    // 预览媒体
    previewMedia() {
      if (this.isImage) {
        this.showImagePreview = true
      } else if (this.isVideo) {
        this.showVideoPreview = true
      } else {
        // 其他文件类型直接在新窗口打开
        window.open(this.mediaUrl, '_blank')
      }
    },
    
    // 下载媒体文件
    async downloadMedia() {
      if (!this.mediaUrl) {
        this.$message.warning('没有可下载的文件')
        return
      }
      
      try {
        const fileName = this.getFileName(this.mediaUrl)
        
        // 使用fetch下载文件
        const response = await fetch(this.mediaUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
        })
        
        if (!response.ok) {
          throw new Error('下载失败')
        }
        
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        
        // 创建下载链接
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 释放URL对象
        window.URL.revokeObjectURL(url)
        
        this.$message.success('下载成功')
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请重试')
      }
    },
    
    // 获取文件名
    getFileName(url) {
      if (!url) return 'unknown'
      const parts = url.split('/')
      const fileName = parts[parts.length - 1]
      return fileName || 'download'
    },
    
    // 处理视频关闭
    handleVideoClose() {
      this.showVideoPreview = false
      this.videoLoading = false
    },
    
    // 处理视频错误
    handleVideoError() {
      this.videoLoading = false
      this.$message.error('视频加载失败')
    },
    
    // 处理图片错误
    handleImageError() {
      this.$message.error('图片加载失败')
    }
  }
}
</script>

<style lang="scss" scoped>
.media-preview {
  position: relative;
  display: inline-block;
  
  .media-container {
    position: relative;
    display: inline-block;
    
    &:hover .media-actions {
      opacity: 1;
    }
  }
  
  .video-container {
    position: relative;
    display: inline-block;
    
    .video-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.3);
      cursor: pointer;
      
      i {
        color: white;
        font-size: 20px;
      }
    }
  }
  
  .file-container {
    width: 60px;
    height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
    
    .file-name {
      font-size: 10px;
      color: #666;
      margin: 2px 0 0 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      width: 100%;
      text-align: center;
    }
  }
  
  .media-actions {
    position: absolute;
    top: -5px;
    right: -5px;
    display: flex;
    gap: 5px;
    opacity: 0;
    transition: opacity 0.3s;
    z-index: 10;
    
    .el-button {
      width: 24px;
      height: 24px;
      padding: 0;
      
      &.is-circle {
        border-radius: 50%;
      }
    }
  }
  
  .no-media {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 12px;
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
  }
  
  .image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 30px;
  }
}

.video-preview-container {
  text-align: center;
  
  .video-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #666;
    
    i {
      font-size: 24px;
      margin-bottom: 10px;
    }
  }
}

.image-preview-container {
  text-align: center;
}

// Element UI 覆盖样式
:deep(.el-image__error) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 14px;
}
</style>
